import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';
import Pagination from '../components/Pagination';
import SEOHead from '../components/SEOHead';
import AdBanner from '../components/AdBanner';
import NativeAdCard from '../components/NativeAdCard';
import { useAds, injectAds } from '../hooks/useAds';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plane, Search, ArrowRight, Users, MessageSquare, Clock, Globe, MapPin, TrendingUp, Plus, X, AlertCircle } from 'lucide-react';
import { API } from '../config';

/** Duration display helper */
function fmtDuration(hrs) {
  if (!hrs) return null;
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Distance display */
function fmtDistance(km) {
  if (!km) return null;
  return km >= 1000 ? `${(km / 1000).toFixed(1)}k km` : `${km} km`;
}

/** Route card */
function RouteCard({ route, index }) {
  const orig = route.origin_airport;
  const dest = route.destination_airport;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.025 }}
    >
      <Link to={`/route/${route.route_id}`} data-testid={`route-card-${route.route_id}`}>
        <Card className="fly-card overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer group">
          {/* Image strip */}
          <div className="relative h-24 overflow-hidden">
            {dest?.image_url ? (
              <img
                src={dest.image_url}
                alt={dest.city}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={e => { e.target.src = 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=60'; }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#FF4500]/20 to-[#1E3A8A]/30" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-white">
                <span className="text-base font-bold drop-shadow" style={{ fontFamily: 'Space Grotesk' }}>
                  {route.origin_code}
                </span>
                <Plane className="w-3 h-3 opacity-70" />
                <span className="text-base font-bold drop-shadow" style={{ fontFamily: 'Space Grotesk' }}>
                  {route.destination_code}
                </span>
              </div>
              {dest?.flag && <span className="text-lg drop-shadow">{dest.flag}</span>}
            </div>
          </div>

          {/* Info */}
          <div className="p-2.5 sm:p-3">
            <div className="min-w-0 mb-1.5">
              <p className="text-xs sm:text-sm font-semibold leading-tight truncate">
                {orig?.city || route.origin_code}
                <span className="text-muted-foreground mx-1">→</span>
                {dest?.city || route.destination_code}
              </p>
              {(orig?.name || dest?.name) && (
                <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 truncate hidden sm:block">
                  {orig?.name ? orig.name.replace(/ International Airport| Airport/g, '').trim() : route.origin_code}
                  <span className="mx-1">→</span>
                  {dest?.name ? dest.name.replace(/ International Airport| Airport/g, '').trim() : route.destination_code}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
              <span className="flex items-center gap-0.5">
                <Users className="w-2.5 h-2.5" /> {route.checkin_count || 0}
              </span>
              <span className="flex items-center gap-0.5">
                <MessageSquare className="w-2.5 h-2.5" /> {route.post_count || 0}
              </span>
            </div>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}

/** Route lookup dialog result */
function RouteLookupDialog({ isOpen, onClose, lookupData, loading, error }) {
  const navigate = useNavigate();
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="route-lookup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="w-5 h-5 text-[#FF4500]" />
            Route Details
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="space-y-3 py-2">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500 py-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {lookupData && !loading && (
          <div className="space-y-4">
            {/* Route header */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40">
              <div className="text-center">
                <p className="text-xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>
                  {lookupData.route.origin_code}
                </p>
                <p className="text-xs text-muted-foreground">{lookupData.origin_airport?.city}</p>
                {lookupData.origin_airport?.flag && <p>{lookupData.origin_airport.flag}</p>}
              </div>
              <div className="flex flex-col items-center gap-1 px-3">
                <Plane className="w-5 h-5 text-muted-foreground" />
                {lookupData.duration_hrs && (
                  <p className="text-xs text-muted-foreground font-medium">{fmtDuration(lookupData.duration_hrs)}</p>
                )}
                {lookupData.distance_km && (
                  <p className="text-[10px] text-muted-foreground">{fmtDistance(lookupData.distance_km)}</p>
                )}
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>
                  {lookupData.route.destination_code}
                </p>
                <p className="text-xs text-muted-foreground">{lookupData.destination_airport?.city}</p>
                {lookupData.destination_airport?.flag && <p>{lookupData.destination_airport.flag}</p>}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 text-center text-xs">
              <div className="p-2 rounded-lg bg-muted/40">
                <p className="text-lg font-bold text-[#FF4500]">{lookupData.route.checkin_count || 0}</p>
                <p className="text-muted-foreground">Flyers</p>
              </div>
              <div className="p-2 rounded-lg bg-muted/40">
                <p className="text-lg font-bold">{lookupData.route.post_count || 0}</p>
                <p className="text-muted-foreground">Community Posts</p>
              </div>
            </div>

            {/* New route badge */}
            {lookupData.is_new && (
              <div className="flex items-center gap-2 p-3 rounded-xl border border-[#FF4500]/30 bg-[#FF4500]/5 text-sm">
                <Plus className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                <span>Be the first to explore this route! A community page will be created for you.</span>
              </div>
            )}

            {/* Posts preview */}
            {lookupData.posts_preview?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">RECENT COMMUNITY POSTS</p>
                <div className="space-y-2">
                  {lookupData.posts_preview.slice(0, 2).map(post => (
                    <div key={post.post_id} className="text-xs p-2 rounded-lg bg-muted/30 line-clamp-2">
                      <span className="font-medium">{post.author_name}:</span> {post.content}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              className="w-full fly-btn-primary"
              onClick={() => {
                onClose();
                navigate(`/route/${lookupData.route.route_id}`);
              }}
              data-testid="goto-route-community-btn"
            >
              <Plane className="w-4 h-4 mr-2" />
              {lookupData.is_new ? 'Create Route Community' : 'Open Route Community'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RoutesPage() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const ads = useAds('routes');

  // Route lookup (origin → destination)
  const [lookupOrigin, setLookupOrigin] = useState('');
  const [lookupDest, setLookupDest] = useState('');
  const [lookupData, setLookupData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const LIMIT = 24;

  // Debounce list search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (debouncedSearch) params.append('search', debouncedSearch);
      const res = await axios.get(`${API}/routes/list?${params}`);
      setRoutes(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  /** Parse "DXB LHR", "DXB-LHR", "DXB to LHR" → [DXB, LHR] */
  function parseRoutePair(input) {
    const clean = input.trim().toUpperCase();
    const sep = clean.match(/[-\s]+TO[\s]+|[-\s]+|→/);
    if (sep) {
      const idx = clean.search(/[-\s]+TO[\s]+|[-→]/);
      const [a, b] = clean.split(/\s*[-→]\s*|\s+TO\s+|\s+/);
      if (a && b && a.length >= 3 && b.length >= 3) return [a.slice(0, 3), b.slice(0, 3)];
    }
    return null;
  }

  const handleLookup = async () => {
    const origTrim = lookupOrigin.trim().toUpperCase().slice(0, 3);
    const destTrim = lookupDest.trim().toUpperCase().slice(0, 3);
    if (!origTrim || !destTrim || origTrim.length < 3 || destTrim.length < 3) {
      setLookupError('Enter valid 3-letter IATA airport codes (e.g. DXB, LHR)');
      setDialogOpen(true);
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    setLookupData(null);
    setDialogOpen(true);
    try {
      const res = await axios.get(`${API}/routes/lookup?origin=${origTrim}&destination=${destTrim}`);
      setLookupData(res.data);
    } catch (e) {
      setLookupError(e.response?.data?.detail || 'Could not find route. Check the airport codes.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // If the search looks like a route pair, do a lookup
    const pair = parseRoutePair(search);
    if (pair) {
      setLookupOrigin(pair[0]);
      setLookupDest(pair[1]);
      setLookupLoading(true);
      setLookupError('');
      setLookupData(null);
      setDialogOpen(true);
      axios.get(`${API}/routes/lookup?origin=${pair[0]}&destination=${pair[1]}`)
        .then(res => { setLookupData(res.data); setLookupLoading(false); })
        .catch(e => { setLookupError(e.response?.data?.detail || 'Route not found'); setLookupLoading(false); });
    }
  };

  return (
    <PageLayout>
      <SEOHead
        title="Flight Routes Community — 250+ Active Routes"
        description="Browse 250+ active flight route communities. Share experiences, seat tips, and travel knowledge for every route worldwide."
        url="/routes"
        keywords="flight routes, route community, flight experiences, seat tips, airline reviews"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <Plane className="w-6 h-6 text-[#FF4500]" />
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Flight Routes
            </h1>
          </div>
          <p className="text-muted-foreground text-sm">
            {total > 0 ? `${total} active route communities` : 'Browse all flight route communities'}
          </p>
        </div>

        {/* ── 2-column layout ── */}
        <div className="flex gap-6 items-start">

          {/* ── Left: Routes Grid ──────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* Mobile Route Finder — top of page on small screens, hidden on lg (uses sidebar) */}
            <Card className="lg:hidden fly-card p-3 border-[#FF4500]/20 mb-4">
              <p className="text-xs font-semibold mb-2 flex items-center gap-1.5 text-[#FF4500]">
                <Search className="w-3.5 h-3.5" /> Find a Specific Route
              </p>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="DXB"
                  value={lookupOrigin}
                  onChange={e => setLookupOrigin(e.target.value.toUpperCase().slice(0, 3))}
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  className="uppercase font-mono text-center font-bold tracking-widest flex-1 h-9 text-base"
                  maxLength={3}
                  data-testid="mobile-route-origin-input"
                />
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <Input
                  placeholder="LHR"
                  value={lookupDest}
                  onChange={e => setLookupDest(e.target.value.toUpperCase().slice(0, 3))}
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  className="uppercase font-mono text-center font-bold tracking-widest flex-1 h-9 text-base"
                  maxLength={3}
                  data-testid="mobile-route-dest-input"
                />
                <Button
                  className="fly-btn-primary flex-shrink-0 h-9 px-3"
                  onClick={handleLookup}
                  disabled={lookupOrigin.length < 3 || lookupDest.length < 3}
                  data-testid="mobile-route-lookup-btn"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </Card>

            {/* Browse + Search */}
            <div className="flex flex-wrap gap-3 mb-5 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <form onSubmit={handleSearchSubmit}>
                  <Input
                    placeholder="Search — e.g. DXB, London, DXB-LHR..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="route-search-input"
                  />
                </form>
              </div>
              {search && (
                <Button variant="ghost" size="sm" onClick={() => setSearch('')}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{total}</span> routes
              </div>
            </div>

            {/* Routes Grid */}
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}
              </div>
            ) : routes.length === 0 ? (
              <div className="text-center py-16">
                <Globe className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-2">No routes found for "{search}"</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Use the Route Finder on the right to look up and create a community for this route
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    const pair = parseRoutePair(search);
                    if (pair) { setLookupOrigin(pair[0]); setLookupDest(pair[1]); handleLookup(); }
                    else setSearch('');
                  }}
                >
                  {parseRoutePair(search) ? 'Look up this route' : 'Clear search'}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {injectAds(routes, ads, 2).map((item, i) =>
                    item.__isAd
                      ? <NativeAdCard key={`ad-${item.ad_id}-${i}`} ad={item} variant="route" />
                      : <RouteCard key={item.route_id} route={item} index={i} />
                  )}
                </div>
                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  limit={LIMIT}
                  onPageChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                />
              </>
            )}
          </div>

          {/* ── Right: Sidebar ─────────────────────────────────── */}
          <aside className="hidden lg:block flex-shrink-0 w-[300px]">
            <div className="sticky top-20 space-y-4">

              {/* Route Finder Card */}
              <Card className="fly-card p-4 border-[#FF4500]/20">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="w-4 h-4 text-[#FF4500]" />
                  <h2 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                    Find a Specific Route
                  </h2>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Enter IATA codes to get details and create the community page.</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="text-[10px] text-muted-foreground mb-1 uppercase font-medium">From</p>
                      <Input
                        placeholder="DXB"
                        value={lookupOrigin}
                        onChange={e => setLookupOrigin(e.target.value.toUpperCase().slice(0, 3))}
                        onKeyDown={e => e.key === 'Enter' && handleLookup()}
                        className="uppercase font-mono text-center text-base font-semibold h-10 tracking-widest"
                        data-testid="route-origin-input"
                        maxLength={3}
                      />
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground mt-5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-[10px] text-muted-foreground mb-1 uppercase font-medium">To</p>
                      <Input
                        placeholder="LHR"
                        value={lookupDest}
                        onChange={e => setLookupDest(e.target.value.toUpperCase().slice(0, 3))}
                        onKeyDown={e => e.key === 'Enter' && handleLookup()}
                        className="uppercase font-mono text-center text-base font-semibold h-10 tracking-widest"
                        data-testid="route-dest-input"
                        maxLength={3}
                      />
                    </div>
                  </div>
                  <Button
                    className="fly-btn-primary w-full h-9"
                    onClick={handleLookup}
                    disabled={lookupOrigin.length < 3 || lookupDest.length < 3}
                    data-testid="route-lookup-btn"
                  >
                    <Search className="w-4 h-4 mr-1.5" /> Find Route
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Tip: search "DXB-LHR" in the browse box too
                </p>
              </Card>

              {/* Ad */}
              {ads.length > 0
                ? <NativeAdCard ad={ads[0]} variant="sidebar" />
                : <AdBanner variant="sidebar" />
              }

              {/* Stats card */}
              <Card className="fly-card p-4">
                <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                  <TrendingUp className="w-4 h-4 text-[#FF4500]" /> Community Stats
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Active Routes</span>
                    <span className="font-semibold">{total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Showing per page</span>
                    <span className="font-semibold">{LIMIT}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pages</span>
                    <span className="font-semibold">{pages}</span>
                  </div>
                </div>
                <Link to="/discover" className="mt-3 text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                  Explore airports →
                </Link>
              </Card>

            </div>
          </aside>



        </div>{/* end 2-col */}

      </div>

      {/* Lookup Result Dialog */}
      <RouteLookupDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        lookupData={lookupData}
        loading={lookupLoading}
        error={lookupError}
      />
    </PageLayout>
  );
}
